/* Sitewide search overlay (Figma 3138:1738).
   One <pf-search-overlay> is mounted from layout/theme.liquid; every header
   search link (data-pf-search-open, or any same-origin link to the bare
   search route) opens it in place of navigating. Quick results come from
   Shopify's predictive search endpoint and are enriched from the catalog map
   the section renders, so each row shows the transparent bottle cutout and
   the card description rather than the featured/editorial shot. */
(function () {
  'use strict';

  if (customElements.get('pf-search-overlay')) return;

  var STORAGE_KEY = 'pf:recent-searches';
  var MAX_RECENT = 5;
  var DEBOUNCE_MS = 180;
  var CLOSE_MS = 260;
  // How many mapped phrases one query may search for on top of itself. A cap
  // rather than a judgement: each one is a separate request.
  var MAX_EXPANSIONS = 2;
  // Products, the categories they sit in, and the journal entries about them —
  // "oils" has to answer with Essential Oils and Body Oils and the posts about
  // them, not with a product list alone.
  var RESOURCE_TYPES = 'product,collection,article';

  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(list)) return [];
      return list
        .filter(function (term) { return typeof term === 'string' && term.trim(); })
        .slice(0, MAX_RECENT);
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) {
      /* private mode / quota: recent searches are a convenience only */
    }
  }

  function rememberSearch(term) {
    term = String(term || '').trim();
    if (!term) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    writeRecent(list);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatPrice(value) {
    var number = Number(value);
    if (!isFinite(number)) return '';
    return 'Rs. ' + number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  class PfSearchOverlay extends HTMLElement {
    connectedCallback() {
      if (this.pfInit) return;
      this.pfInit = true;

      this.searchUrl = (this.dataset.searchUrl || '/search').replace(/\/$/, '');
      this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      this.limit = parseInt(this.dataset.limit, 10) || 5;
      this.icons = { history: this.dataset.iconHistory || '', arrow: this.dataset.iconArrow || '' };

      this.panel = this.querySelector('.pf-search__panel');
      this.form = this.querySelector('[data-pf-search-form]');
      this.input = this.querySelector('[data-pf-search-input]');
      this.clearBtn = this.querySelector('[data-pf-search-clear]');
      this.resultsSection = this.querySelector('[data-pf-search-results]');
      this.resultsList = this.querySelector('[data-pf-search-results-list]');
      this.emptyLine = this.querySelector('[data-pf-search-empty]');
      this.recentSection = this.querySelector('[data-pf-search-recent]');
      this.recentList = this.querySelector('[data-pf-search-recent-list]');
      this.catalog = this.readCatalog();
      this.keywords = this.readKeywords();
      this.labels = {
        collection: this.dataset.collectionLabel || 'Category',
        article: this.dataset.articleLabel || 'Journal'
      };

      this.lastTrigger = null;
      this.abort = null;
      this.timer = null;
      this.closeTimer = null;
      this.query = '';

      var self = this;
      document.addEventListener('click', function (e) { self.onDocumentClick(e); });
      this.querySelectorAll('[data-pf-search-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });
      this.clearBtn.addEventListener('click', function () { self.onClear(); });
      this.input.addEventListener('input', function () { self.onInput(false); });
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
      this.addEventListener('keydown', function (e) { self.onKeydown(e); });
      this.resultsList.addEventListener('click', function (e) {
        if (e.target.closest('a')) rememberSearch(self.query);
      });
      this.recentList.addEventListener('click', function (e) { self.onRecentClick(e); });

      var params = new URLSearchParams(window.location.search);

      // The results page counts as a search, so a submit that never touched
      // this overlay (no JS, a shared link) still lands in Recent searches.
      if (window.location.pathname.replace(/\/$/, '') === this.searchUrl && params.get('q')) {
        rememberSearch(params.get('q'));
      }

      // Deep-link / test hook: ?pf-search=1 opens empty, ?pf-search=<term>
      // opens with that query typed; #pf-search opens empty.
      if (params.has('pf-search') || window.location.hash === '#pf-search') {
        var seed = params.get('pf-search');
        this.open(null);
        if (seed && seed !== '1') {
          this.input.value = seed;
          this.onInput(true);
        }
      }
    }

    readCatalog() {
      var node = this.querySelector('[data-pf-search-catalog]');
      var map = {};
      if (!node) return map;
      try {
        (JSON.parse(node.textContent) || []).forEach(function (p) {
          if (p && p.handle) map[p.handle] = p;
        });
      } catch (e) {
        /* malformed catalog: rows fall back to the endpoint's own fields */
      }
      return map;
    }

    /* The keyword map, as the section renders it: one "typed word: what it
       should also search for" rule per line, comma-separating several. Kept as
       a plain object of word -> [phrase], so a rule can be added in the theme
       editor without touching this file. */
    readKeywords() {
      var node = this.querySelector('[data-pf-search-keywords]');
      var map = {};
      if (!node) return map;
      var raw = '';
      try {
        raw = JSON.parse(node.textContent);
      } catch (e) {
        raw = node.textContent;
      }
      String(raw || '').split(/[\r\n]+/).forEach(function (line) {
        var split = line.indexOf(':');
        if (split === -1) return;
        var word = line.slice(0, split).trim().toLowerCase();
        if (!word) return;
        var phrases = line.slice(split + 1).split(',')
          .map(function (t) { return t.trim(); })
          .filter(Boolean);
        if (phrases.length) map[word] = phrases;
      });
      return map;
    }

    /* Every mapped phrase the typed words ask for, in the order they were
       typed and without repeating one. The query itself is always searched
       first; these ride behind it. */
    expansionsFor(q) {
      var self = this;
      var seen = Object.create(null);
      var out = [];
      var words = String(q).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      // The whole phrase counts as a word too, so "body oil" can have a rule
      // of its own that a single word does not.
      words.unshift(String(q).trim().toLowerCase());
      words.forEach(function (word) {
        (self.keywords[word] || []).forEach(function (phrase) {
          var key = phrase.toLowerCase();
          if (key === String(q).trim().toLowerCase() || seen[key]) return;
          seen[key] = true;
          out.push(phrase);
        });
      });
      return out.slice(0, MAX_EXPANSIONS);
    }

    get isOpen() {
      return !this.hidden;
    }

    isOpener(el) {
      if (el.hasAttribute('data-pf-search-open')) return true;
      if (this.contains(el)) return false;
      var href = el.getAttribute('href');
      if (!href) return false;
      try {
        var url = new URL(href, window.location.origin);
        return url.origin === window.location.origin
          && url.pathname.replace(/\/$/, '') === this.searchUrl
          && !url.search;
      } catch (e) {
        return false;
      }
    }

    onDocumentClick(e) {
      var el = e.target.closest('a[href], button[data-pf-search-open]');
      if (!el || !this.isOpener(el)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      e.preventDefault();
      this.open(el);
    }

    open(trigger) {
      clearTimeout(this.closeTimer);
      if (this.isOpen) {
        this.input.focus();
        return;
      }
      this.lastTrigger = trigger || (document.activeElement !== document.body ? document.activeElement : null);
      if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
      this.hidden = false;
      document.documentElement.classList.add('pf-search-open');
      this.renderRecent();
      this.syncState();
      // A forced style flush, not requestAnimationFrame: the desktop homepage
      // starves the frame loop (see pf-motion.js), and a reveal that waits on
      // a frame can leave the panel mounted but invisible. The flush commits
      // the hidden state so the opacity transition still plays.
      void this.panel.offsetWidth;
      this.classList.add('is-open');
      this.input.focus();
    }

    close() {
      if (!this.isOpen) return;
      clearTimeout(this.timer);
      if (this.abort) this.abort.abort();
      this.classList.remove('is-open');
      document.documentElement.classList.remove('pf-search-open');
      var self = this;
      var finish = function () { self.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else this.closeTimer = setTimeout(finish, CLOSE_MS);
      var trigger = this.lastTrigger;
      this.lastTrigger = null;
      if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'false');
      if (trigger && trigger.focus) trigger.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusables = this.focusables();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    focusables() {
      var nodes = this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])');
      return Array.prototype.filter.call(nodes, function (el) { return el.offsetParent !== null; });
    }

    onInput(immediate) {
      var q = this.input.value.trim();
      this.query = q;
      this.syncState();
      clearTimeout(this.timer);
      if (!q) {
        if (this.abort) this.abort.abort();
        this.renderResults([], '');
        return;
      }
      // Catalog matches paint at once; the endpoint's answer replaces them.
      var local = this.localMatches(q);
      if (local.length) this.renderResults(local, q);
      var self = this;
      this.timer = setTimeout(function () { self.fetchSuggestions(q); }, immediate ? 0 : DEBOUNCE_MS);
    }

    syncState() {
      var hasQuery = !!this.query;
      this.resultsSection.hidden = !hasQuery;
      this.clearBtn.setAttribute('aria-label', hasQuery ? 'Clear search' : 'Close search');
      this.classList.toggle('has-query', hasQuery);
    }

    suggestUrlFor(term) {
      return this.suggestUrl + '.json?q=' + encodeURIComponent(term)
        + '&resources[type]=' + RESOURCE_TYPES
        + '&resources[limit]=' + this.limit
        + '&resources[limit_scope]=each'
        + '&resources[options][unavailable_products]=last';
    }

    /* The typed query first, then each phrase the keyword map sends it to.
       Everything is merged into one list, deduplicated by URL, so a mapped
       search reads as one set of answers rather than three. */
    fetchSuggestions(q) {
      if (this.abort) this.abort.abort();
      var controller = this.abort = new AbortController();
      var self = this;
      var terms = [q].concat(this.expansionsFor(q));

      var requests = terms.map(function (term) {
        return window.fetch(self.suggestUrlFor(term), {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .catch(function () { return null; });
      });

      Promise.all(requests).then(function (payloads) {
        if (controller.signal.aborted || self.query !== q) return;
        var items = [];
        var seen = Object.create(null);
        var answered = false;

        function push(item) {
          if (!item || !item.url || seen[item.url]) return;
          seen[item.url] = true;
          items.push(item);
        }

        // Categories lead, then products, then the journal — the shopper
        // asking for "oils" wants the shelf before a single bottle. Each kind
        // is capped so a mapped query cannot bury the products under the
        // categories and posts it also matched.
        var caps = { collections: 2, products: self.limit, articles: 2 };
        ['collections', 'products', 'articles'].forEach(function (kind) {
          var taken = 0;
          payloads.forEach(function (data) {
            if (!data) return;
            answered = true;
            var results = (((data || {}).resources || {}).results || {})[kind] || [];
            results.forEach(function (entry) {
              if (taken >= caps[kind]) return;
              var before = items.length;
              push(self.toItem(entry, kind));
              if (items.length > before) taken += 1;
            });
          });
        });

        if (!answered) {
          self.renderResults(self.localMatches(q), q);
          return;
        }
        self.renderResults(items, q);
      });
    }

    toItem(entry, kind) {
      if (kind === 'products') return this.enrich(entry);
      var image = entry.image
        || (entry.featured_image && (entry.featured_image.url || entry.featured_image))
        || '';
      return {
        url: entry.url || '',
        title: entry.title || '',
        subtitle: kind === 'collections' ? this.labels.collection : this.labels.article,
        price: '',
        image: typeof image === 'string' ? image : '',
        cover: true
      };
    }

    enrich(p) {
      var c = this.catalog[p.handle] || {};
      return {
        url: p.url || c.url || '#',
        title: p.title || c.title || '',
      titleName: p.titleName || c.titleName || '',
      titleCategory: p.titleCategory || c.titleCategory || '',
        subtitle: c.subtitle || '',
        price: c.price || formatPrice(p.price_max || p.price),
        image: c.image || (p.featured_image && p.featured_image.url) || p.image || ''
      };
    }

    localMatches(q) {
      var needle = q.toLowerCase();
      var self = this;
      return Object.keys(this.catalog)
        .map(function (handle) { return self.catalog[handle]; })
        .filter(function (p) {
          return (p.title || '').toLowerCase().indexOf(needle) !== -1
            || (p.subtitle || '').toLowerCase().indexOf(needle) !== -1;
        })
        .slice(0, this.limit);
    }

    renderResults(items, q) {
      this.resultsList.innerHTML = items.map(function (p) {
        var price = p.price || '';
        var short = price.replace(/\.00$/, '');
        return '<li>'
          + '<a class="pf-search__result" href="' + escapeHtml(p.url) + '">'
          + '<span class="pf-search__result-media' + (p.cover ? ' pf-search__result-media--cover' : '') + '">'
          + (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="" width="54" height="54">' : '')
          + '</span>'
          + '<span class="pf-search__result-info">'
          + '<span class="pf-search__result-title">'
          +   '<span class="pf-search__result-title-name">' + escapeHtml(p.titleName || p.title) + '</span>'
          +   (p.titleCategory ? '<span class="pf-search__result-title-category">' + escapeHtml(p.titleCategory) + '</span>' : '')
          + '</span>'
          + '<span class="pf-search__result-sub">' + escapeHtml(p.subtitle) + '</span>'
          + '</span>'
          + '<span class="pf-search__result-price">'
          + '<span class="pf-search__price-full">' + escapeHtml(price) + '</span>'
          + '<span class="pf-search__price-short">' + escapeHtml(short) + '</span>'
          + '</span>'
          + '</a>'
          + '</li>';
      }).join('');
      var none = !!q && !items.length;
      this.emptyLine.hidden = !none;
      if (none) this.emptyLine.textContent = 'No results for “' + q + '”';
    }

    renderRecent() {
      var list = readRecent();
      var self = this;
      this.recentSection.hidden = !list.length;
      this.recentList.innerHTML = list.map(function (term) {
        var safe = escapeHtml(term);
        return '<li>'
          + '<div class="pf-search__recent">'
          + '<button type="button" class="pf-search__recent-term" data-pf-search-term="' + safe + '">'
          + '<img src="' + escapeHtml(self.icons.history) + '" alt="" width="16" height="16">'
          + '<span>' + safe + '</span>'
          + '</button>'
          + '<button type="button" class="pf-search__recent-fill" data-pf-search-fill="' + safe + '" aria-label="Edit search: ' + safe + '">'
          + '<img src="' + escapeHtml(self.icons.arrow) + '" alt="" width="14" height="14">'
          + '</button>'
          + '</div>'
          + '</li>';
      }).join('');
    }

    onRecentClick(e) {
      var term = e.target.closest('[data-pf-search-term]');
      if (term) {
        this.submit(term.getAttribute('data-pf-search-term'));
        return;
      }
      var fill = e.target.closest('[data-pf-search-fill]');
      if (fill) {
        var value = fill.getAttribute('data-pf-search-fill') || '';
        this.input.value = value;
        this.input.focus();
        this.input.setSelectionRange(value.length, value.length);
        this.onInput(true);
      }
    }

    onSubmit(e) {
      var q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
        return;
      }
      rememberSearch(q);
      /* the native GET to /search?q= carries on */
    }

    submit(term) {
      rememberSearch(term);
      window.location.assign(this.searchUrl + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last');
    }

    onClear() {
      if (this.input.value) {
        this.input.value = '';
        this.onInput(true);
        this.input.focus();
      } else {
        this.close();
      }
    }
  }

  customElements.define('pf-search-overlay', PfSearchOverlay);
})();

